import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { AES } from "crypto-js";
import userCategory from "../../constants/userCategory.js";
import userTypeConsts from "../../constants/userTypeConsts.js";
import "./MobileGlobalSearch.css";

const highlightMatch = (text, query) => {
  if (!query?.trim() || !text) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = String(text).split(new RegExp(`(${escaped})`, "gi"));
  const q = query.toLowerCase();
  return parts.map((part, index) =>
    part.toLowerCase() === q ? (
      <strong key={index} className="mobile-global-search-highlight">
        {part}
      </strong>
    ) : (
      <React.Fragment key={index}>{part}</React.Fragment>
    )
  );
};

const encryptRouteId = (id) =>
  encodeURIComponent(AES.encrypt(id, "encryptionKey").toString()).replace(
    /\//g,
    "%2F"
  );

const MobileGlobalSearch = ({ isOpen, onClose }) => {
  const user = useSelector((state) => state.auth.user);
  const { appSettings } = useSelector((state) => state.appSettings);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const inputRef = useRef(null);

  const isAdmin =
    user?.userType === userTypeConsts.SUPER_ADMIN ||
    user?.userType === userTypeConsts.ADMIN;
  const isIndividualOrBusiness =
    user?.userCategory === userCategory.Individual ||
    user?.userCategory === userCategory.Business;
  const isVendor = user?.userCategory === userCategory.Vendor;
  const isClient = user?.userCategory === userCategory.Client;

  const pages = useMemo(() => {
    const items = [];
    const lang = appSettings?.language;

    if (isVendor) {
      items.push(
        { id: "page-dashboard", title: "Dashboard", subtitle: "Go to Dashboard", path: "/vendor/dashboard", type: "page" },
        { id: "page-invoices", title: "Invoices", subtitle: "Vendor invoices", path: "/vendor/invoice", type: "page" }
      );
      return items;
    }

    if (isClient) {
      items.push(
        { id: "page-dashboard", title: "Dashboard", subtitle: "Go to Dashboard", path: "/client/dashboard", type: "page" },
        { id: "page-invoices", title: "Invoices", subtitle: "Client invoices", path: "/client/invoice", type: "page" }
      );
      return items;
    }

    items.push({
      id: "page-dashboard",
      title: global.config.locate(lang, "Dashboard") || "Dashboard",
      subtitle: "Go to Dashboard",
      path: "/user/dashboard",
      type: "page",
    });

    if (isAdmin) {
      items.push({
        id: "page-team",
        title: global.config.locate(lang, "My Team") || "My Team",
        subtitle: "Team members",
        path: "/user/myteam/list",
        type: "page",
      });
    }

    if (isIndividualOrBusiness) {
      items.push(
        {
          id: "page-invoices",
          title: global.config.locate(lang, "Invoices") || "Invoices",
          subtitle: "Invoices overview",
          path: "/user/invoices",
          type: "page",
        },
        {
          id: "page-time-tracker",
          title: global.config.locate(lang, "Time_Tracker") || "Time Tracker",
          subtitle: "Time Tracker overview",
          path: "/user/time-tracker/overview",
          type: "page",
        },
        {
          id: "page-time-off",
          title: global.config.locate(lang, "Time_Off") || "Time Off",
          subtitle: "Time Off overview",
          path: "/user/time-off/overview",
          type: "page",
        },
        {
          id: "page-calendar",
          title: global.config.locate(lang, "Calendar") || "Calendar",
          subtitle: "Calendar",
          path: "/user/calendar",
          type: "page",
        },
        {
          id: "page-documents",
          title: global.config.locate(lang, "Documents") || "Documents",
          subtitle: "Documents",
          path: "/user/documents/overview",
          type: "page",
        }
      );
    }

    if (
      isAdmin ||
      user?.userType === userTypeConsts.PROJECT_MANAGER
    ) {
      items.push({
        id: "page-expenses",
        title: global.config.locate(lang, "Expenses") || "Expenses",
        subtitle: "Expenses overview",
        path: "/user/expense/overview",
        type: "page",
      });
    }

    if (isAdmin) {
      items.push(
        {
          id: "page-equipment",
          title: global.config.locate(lang, "Equipment") || "Equipment",
          subtitle: "Equipment overview",
          path: "/user/equipment/overview",
          type: "page",
        },
        {
          id: "page-clients",
          title: "Clients",
          subtitle: "Clients report",
          path: "/report/clients/overview",
          type: "page",
        },
        {
          id: "page-settings",
          title: "Account Settings",
          subtitle: "Account settings",
          path: "/user/account/settings",
          type: "page",
        }
      );
    } else if (isIndividualOrBusiness) {
      items.push({
        id: "page-equipment",
        title: global.config.locate(lang, "Equipment") || "Equipment",
        subtitle: "My equipment",
        path: "/user/users/equipment",
        type: "page",
      });
    }

    items.push({
      id: "page-profile",
      title: "Profile Settings",
      subtitle: "Your profile",
      path: "/user/profile/settings",
      type: "page",
    });

    return items;
  }, [
    appSettings?.language,
    isAdmin,
    isClient,
    isIndividualOrBusiness,
    isVendor,
    user?.userType,
  ]);

  const getNotificationMessage = useCallback(
    (item) => {
      if (!item?.notification_data?.[0]) return "";
      if (isAdmin) {
        return (
          item.notification_data[0].message ||
          item.notification_data[0].user_message ||
          ""
        );
      }
      return (
        item.notification_data[0].user_message ||
        item.notification_data[0].message ||
        ""
      );
    },
    [isAdmin]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    setQuery("");
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    document.body.classList.add("mobile-global-search-open");

    return () => {
      clearTimeout(timer);
      document.body.classList.remove("mobile-global-search-open");
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !user?._id || !user?.token) return undefined;

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const requests = [
          axios.post(
            "/api/notification/unread/count",
            { user_id: user._id },
            { headers: { Authorization: `Bearer ${user.token}` } }
          ),
        ];

        if (isAdmin) {
          requests.push(
            axios.get("/api/users?page=1&limit=50", {
              headers: { Authorization: `Bearer ${user.token}` },
            })
          );
        }

        const responses = await Promise.allSettled(requests);
        if (cancelled) return;

        const notifRes = responses[0];
        if (notifRes.status === "fulfilled") {
          const data = notifRes.value.data;
          const list = Array.isArray(data)
            ? data
            : Array.isArray(data?.notifications)
              ? data.notifications
              : [];
          setNotifications(list);
        } else {
          setNotifications([]);
        }

        if (isAdmin && responses[1]?.status === "fulfilled") {
          const userData = responses[1].value.data;
          const list = Array.isArray(userData)
            ? userData
            : Array.isArray(userData?.users)
              ? userData.users
              : Array.isArray(userData?.data)
                ? userData.data
                : [];
          setUsers(list);
        } else {
          setUsers([]);
        }
      } catch (error) {
        if (!cancelled) {
          setNotifications([]);
          setUsers([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, user?._id, user?.token, isAdmin]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    const matchedPages = pages
      .filter(
        (page) =>
          page.title.toLowerCase().includes(q) ||
          page.subtitle.toLowerCase().includes(q)
      )
      .map((page) => ({
        ...page,
        primary: page.title,
        secondary: page.subtitle,
      }));

    const matchedNotifications = notifications
      .map((item) => {
        const message = getNotificationMessage(item);
        return {
          id: `notif-${item._id}`,
          type: "notification",
          primary: message,
          secondary: "Notification",
          path: item.url || "/user/dashboard",
        };
      })
      .filter((item) => item.primary.toLowerCase().includes(q));

    const matchedUsers = users
      .map((item) => {
        const name = [item.first_name, item.last_name].filter(Boolean).join(" ").trim()
          || item.company_name
          || item.email
          || "User";
        return {
          id: `user-${item._id}`,
          type: "user",
          primary: name,
          secondary: item.email || "Team member",
          path: `/user/profile/${encryptRouteId(item._id)}`,
        };
      })
      .filter(
        (item) =>
          item.primary.toLowerCase().includes(q) ||
          item.secondary.toLowerCase().includes(q)
      );

    return [...matchedPages, ...matchedUsers, ...matchedNotifications].slice(0, 40);
  }, [query, pages, notifications, users, getNotificationMessage]);

  const handleClose = () => {
    setQuery("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="mobile-global-search" role="dialog" aria-modal="true" aria-label="Search">
      <div className="mobile-global-search-header">
        <h2 className="mobile-global-search-title">Search</h2>
        <button
          type="button"
          className="mobile-global-search-close"
          aria-label="Close search"
          onClick={handleClose}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path
              d="M1 1L13 13M13 1L1 13"
              stroke="#6A7988"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="mobile-global-search-field-wrap">
        <div className="mobile-global-search-field">
          <svg
            className="mobile-global-search-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M23.7921 22.7103L16.5466 15.5328C19.7292 11.8397 19.5484 6.26379 16.0403 2.75172C14.232 0.905172 11.8089 0 9.42194 0C7.03499 0 4.61188 0.905172 2.76742 2.75172C-0.921497 6.44483 -0.921497 12.3828 2.76742 16.0759C4.57571 17.8862 6.99883 18.8276 9.42194 18.8276C11.5919 18.8276 13.798 18.0672 15.534 16.5466L22.7794 23.7603C22.8879 23.869 23.0687 23.9414 23.2496 23.9414C23.4304 23.9414 23.6112 23.869 23.7559 23.7241C24.0452 23.4707 24.0452 23 23.7921 22.7103ZM3.78007 15.0259C2.2611 13.5414 1.46545 11.5138 1.46545 9.41379C1.46545 7.31379 2.29727 5.28621 3.78007 3.76552C5.29903 2.24483 7.28816 1.44828 9.42194 1.44828C11.5557 1.44828 13.5449 2.28103 15.0638 3.76552C16.5466 5.28621 17.3784 7.27759 17.3784 9.41379C17.3784 11.55 16.5466 13.5414 15.0638 15.0621C13.5449 16.5466 11.5196 17.3793 9.42194 17.3793C7.32432 17.3793 5.29903 16.5466 3.78007 15.0259Z"
              fill="#6A7988"
            />
          </svg>
          <input
            ref={inputRef}
            type="text"
            className="mobile-global-search-input"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search the site"
          />
          {query.trim().length > 0 && (
            <button
              type="button"
              className="mobile-global-search-clear"
              aria-label="Clear search"
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
            >
              <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path
                  d="M1 1L13 13M13 1L1 13"
                  stroke="#6A7988"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {query.trim().length > 0 && (
        <p className="mobile-global-search-status">
          Showing results for &ldquo;{query.trim()}&rdquo;
        </p>
      )}

      <ul className="mobile-global-search-list">
        {isLoading && query.trim().length > 0 ? (
          <li className="mobile-global-search-empty">Searching…</li>
        ) : query.trim().length === 0 ? (
          <li className="mobile-global-search-empty">
            Search pages, people, and notifications
          </li>
        ) : results.length === 0 ? (
          <li className="mobile-global-search-empty">No results found</li>
        ) : (
          results.map((item) => (
            <li key={item.id} className="mobile-global-search-item">
              <Link to={item.path} onClick={handleClose}>
                <div className="mobile-global-search-item-text">
                  <div className="mobile-global-search-item-primary">
                    {highlightMatch(item.primary, query)}
                  </div>
                  {item.secondary && (
                    <div className="mobile-global-search-item-secondary">
                      {item.secondary}
                    </div>
                  )}
                </div>
                <span className="mobile-global-search-chevron" aria-hidden="true">
                  <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                    <path
                      d="M1 1L7 7L1 13"
                      stroke="#405BFF"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Link>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};

export default MobileGlobalSearch;
